import { KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NbAutocompleteModule, NbButtonModule, NbCardModule, NbInputModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DDBCleanScoreRegion, QACleanScoreItem } from 'src/app/models/qa-models/smart-clean';
import { Robot } from 'src/app/models/robot';
import { SharedModule } from 'src/app/shared/shared.module';
import { LoadAllRobots } from '../redux';
import { QaState } from '../redux/qa.state';
import { SmartCleanService } from '../services/smart-clean.service';
import { CleanScoreState, LoadSmartCleanRecords, SmartCleanRecords } from './redux';

interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-clean-score',
  templateUrl: './clean-score.component.html',
  standalone: true,
  imports: [SharedModule, NbCardModule, NbButtonModule, NbInputModule, NbAutocompleteModule]
})
export class CleanScoreComponent implements OnInit, OnDestroy {
  public robot$: Observable<any[]> = inject(Store).select(QaState.robots);
  public smartCleanRecord$: Observable<SmartCleanRecords | undefined> = inject(Store).select(
    CleanScoreState.smartCleanRecords
  );
  private unsubscribe = new Subject<void>();

  public robots: SelectOption[] = [];
  public filteredRobots: SelectOption[] = [];
  public selectedRobot = '';
  public selectedLabel = '';
  public smartCleanRecords?: SmartCleanRecords = undefined;
  public maps: Record<string, DDBCleanScoreRegion[]> = {};
  public changed = new Set<string>();
  public saving = false;

  constructor(
    private store: Store,
    private smartCleanService: SmartCleanService
  ) {
    if (this.store.selectSnapshot(QaState.robots).length < 1) {
      this.store.dispatch(new LoadAllRobots());
    }
  }

  public ngOnInit() {
    this.robot$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      data.forEach((robot: Robot) => {
        this.robots.push({ value: robot.id, label: `${robot.id} (${robot.name})` });
      });
      this.filteredRobots = [...this.robots];
    });
    this.smartCleanRecord$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      data?.regions.forEach((region) => {
        let mapId = region.pmap_id;
        if (region.p2map_id) {
          mapId = region.p2map_id;
        }
        if (mapId) {
          if (!this.maps[mapId]) {
            this.maps[mapId] = [];
          }
          this.maps[mapId].push({ ...region });
        }
      });
      Object.keys(this.maps).forEach((mapId) => {
        this.maps[mapId].sort((a, b) => Number(a.pk.split('#rid#')[1]) - Number(b.pk.split('#rid#')[1]));
      });
      this.smartCleanRecords = data;
    });
  }

  public ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public filterRobots(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const searchText = inputElement.value;

    if (!searchText) {
      this.filteredRobots = [...this.robots];
    } else {
      const filter = searchText.toLowerCase();
      this.filteredRobots = this.robots.filter((robot) => robot.label.toLowerCase().includes(filter));
    }
  }

  public onSelect(option: SelectOption) {
    this.selectedRobot = option.value;
    this.selectedLabel = option.label;

    this.smartCleanRecords = undefined;
    this.maps = {};
    this.changed = new Set<string>();
    this.store.dispatch(new LoadSmartCleanRecords(this.selectedRobot));
  }

  public reverseOrder(a: KeyValue<string, any>, b: KeyValue<string, any>) {
    return a.key > b.key ? -1 : b.key > a.key ? 1 : 0;
  }

  public markChanged(pk: string) {
    this.changed.add(pk);
  }

  public async save() {
    this.saving = true;
    const requests: Record<string, QACleanScoreItem> = {};
    for (const pk of this.changed) {
      for (const regions of Object.values(this.maps)) {
        const region = regions.find((region: any) => region.pk === pk);
        if (region) {
          if (region.pmap_id) {
            if (!requests[region.pmap_id]) {
              requests[region.pmap_id] = { pmap_id: region.pmap_id, regions: [] };
            }
            requests[region.pmap_id].regions.push({
              region_id: region.pk.split('#rid#')[1],
              clean_score: region.region_score
            });
          } else if (region.p2map_id) {
            if (!requests[region.p2map_id]) {
              requests[region.p2map_id] = { p2map_id: region.p2map_id, regions: [] };
            }
            requests[region.p2map_id].regions.push({
              region_id: region.pk.split('#rid#')[1],
              clean_score: region.region_score
            });
          }
          break;
        }
      }
    }
    for (const body of Object.values(requests)) {
      if (body.pmap_id) {
        await this.smartCleanService.patchCleanScoreAmplify(body);
      } else if (body.p2map_id) {
        await this.smartCleanService.patchCleanScoreP2mapAmplify(body);
      }
    }
    this.saving = false;
    this.smartCleanRecords = undefined;
    this.maps = {};
    this.changed = new Set<string>();
    this.store.dispatch(new LoadSmartCleanRecords(this.selectedRobot));
  }
}
